import React, { useState, useEffect } from 'react'
import { useTranslation, Trans } from 'react-i18next'
import { OmSpinner, omNotification } from '@owma/kit'
import { getEnvParam } from 'env'

import styles from './style.module.scss'

import menuCopyIcon from 'assets/images/menu-copy.svg'

import mailboxApiHelper from 'services/mailbox-api'
import { RestClientError } from 'services/restClient'

const kPasswordLength = 16
const kInitialPassword = String('*').repeat(kPasswordLength)

interface AppPasswordPanelProps {
  active: boolean
}

const AppPasswordPanel: React.FC<AppPasswordPanelProps> = ({ active }) => {
  const { t } = useTranslation()
  const [password, setPassword] = useState(kInitialPassword)
  const [isPasswordVisible, setIsPasswordVisible] = useState(false)
  const [isSaved, setIsSaved] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const email = getEnvParam('userEmail')

  useEffect(() => {
    if (!active) {
      setPassword(kInitialPassword)
      setIsPasswordVisible(false)
      setIsSaved(false)
    }
  }, [active])

  const generatePassword = (): void => {
    const lowerCaseChars = 'abcdefghijklmnopqrstuvwxyz'
    const upperCaseChars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'
    const numberChars = '0123456789'

    let possibleChar = ''
    let generatedPassword = ''

    possibleChar += numberChars
    possibleChar += lowerCaseChars
    possibleChar += upperCaseChars

    for (let i = 0; i < kPasswordLength; i += 1) {
      const idx = Math.floor(Math.random() * possibleChar.length)
      generatedPassword += possibleChar[idx]
    }
    setPassword(generatedPassword)
  }

  const handleGenerate = (): void => {
    generatePassword()
    setIsPasswordVisible(true)
    setIsSaved(false)
  }

  const handleCopy = (): void => {
    navigator.clipboard.writeText(password)
  }

  const handleSave = async (): Promise<void> => {
    setIsLoading(true)
    try {
      await mailboxApiHelper.putPasswd({
        old_pw: '',
        new_pw: password,
      })
      setIsSaved(true)
      omNotification.success({
        message: t('settings.accountTabs.appPasswordSaveSuccess'),
        description: t('settings.accountTabs.appPasswordSaveSuccessDesc'),
      })
    } catch (error) {
      // 401(세션만료→로그아웃)·429(요청한도)·IP 차단(→로그아웃 리다이렉트)은
      // 공통 레이어가 이미 사용자에게 통지/전환하므로 중복 알림을 띄우지 않는다.
      const alreadyNotified =
        (error instanceof RestClientError && (error.status === 401 || error.status === 429)) ||
        (error instanceof Error && error.message === 'ip-not-allowed')
      if (!alreadyNotified) {
        omNotification.error({
          message: t('settings.accountTabs.appPasswordSaveFailed'),
          description: t('settings.accountTabs.appPasswordSaveFailedDesc'),
        })
      }
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className={styles.appPasswordPanel}>
      <div className={styles.header}>
        <h2 className={styles.title}>{t('settings.accountTabs.appPassword')}</h2>
      </div>

      <OmSpinner size="large" spinning={isLoading}>
        <div className={styles.content}>
          <div className={styles.description}>
            {t('settings.accountTabs.appPasswordHeadDesc1')}
            <br />
            <div className={styles.note}>{t('settings.accountTabs.appPasswordHeadDesc2')}</div>
          </div>

          <div className={styles.formGroup}>
            <div className={styles.label}>Account:</div>
            <div className={styles.email}>{email}</div>
          </div>

          <div className={styles.formGroup}>
            <div className={styles.label}>Password:</div>
            <div className={styles.passwordGroup}>
              <div className={styles.password_copy}>
                <div className={styles.password}>{password}</div>
                {isPasswordVisible && (
                  <button onClick={handleCopy} className={styles.copyButton}>
                    <img src={menuCopyIcon} alt="copy" />
                  </button>
                )}
              </div>
              <button onClick={handleGenerate} className={styles.generateButton}>
                {t('settings.accountTabs.appPasswordGenerate')}
              </button>
            </div>
          </div>

          <div className={styles.footer}>
            <div>{t('settings.accountTabs.appPasswordFootDesc1')}</div>
            <div>
              <Trans
                i18nKey="settings.accountTabs.appPasswordFootDesc2"
                components={{
                  appLink: (
                    <a
                      href="https://app.officepassword.kr"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {t('settings.accountTabs.passwordAppName')}
                    </a>
                  ),
                }}
              />
            </div>

            {isPasswordVisible && !isSaved && (
              <button className={styles.saveButton} onClick={handleSave}>
                {t('settings.accountTabs.appPasswordSave')}
              </button>
            )}
          </div>
        </div>
      </OmSpinner>
    </div>
  )
}

export default AppPasswordPanel
